import { useEffect, useMemo } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import { Controller, Resolver, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { number, object, string } from 'yup';

import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import { LanguageKeys } from 'src/common/constants/languages';
import { calculateRegistrationPrice, getRegistrationDisplayPrice } from 'src/helpers/registrationPrice';
import { sortDistanceIds } from 'src/helpers/statisticExtras';
import { resolveResourceId } from 'src/helpers/trackMapCompetitionIcons';
import { IEditionCompetition } from 'src/interfaces/Edition';
import { IParticipant } from 'src/interfaces/Participant';
import { IFormRegistration, IRegistration } from 'src/interfaces/Registration';
import { TechnicalType } from 'src/interfaces/TechnicalType';
import useSystemOptionsStore from 'src/stores/systemOptionsStore';

function distanceIdsForCompetition(
  editionCompetitions: IEditionCompetition[],
  competitionId: string,
  distancesById: Record<string, Pick<TechnicalType, 'name' | 'i18n'>>,
  extraDistanceId?: string,
): string[] {
  const fromCompetition = (
    editionCompetitions.find(
      entry => resolveResourceId(entry.competitionId) === resolveResourceId(competitionId),
    )?.distances ?? []
  )
    .map(id => resolveResourceId(id))
    .filter(Boolean);

  const ids =
    extraDistanceId && !fromCompetition.includes(extraDistanceId)
      ? [...fromCompetition, extraDistanceId]
      : fromCompetition;

  return sortDistanceIds(ids, distancesById);
}

interface RegistrationFormProps {
  handleClose: () => void;
  handleSave: (registration: IFormRegistration) => void;
  selectedParticipant?: Partial<IParticipant> & { id: string };
  existingRegistration?: Partial<IRegistration>;
  /** Admin on system participants page may edit and submit price */
  priceEditable?: boolean;
  /** Edition to register for; defaults to system activeEditionId */
  editionId?: string;
}

const RegistrationForm = ({
  handleClose,
  handleSave,
  selectedParticipant,
  existingRegistration,
  priceEditable = false,
  editionId: editionIdProp,
}: RegistrationFormProps) => {
  const {
    t,
    i18n: { resolvedLanguage },
  } = useTranslation();
  const currentLang = resolvedLanguage as LanguageKeys;

  const { activeEditionId, competitionsById, distancesById, editionsById } = useSystemOptionsStore(
    systemOptionsState => systemOptionsState,
  );

  const editionId = editionIdProp || activeEditionId;
  const edition = editionsById && editionId ? editionsById[editionId] : undefined;
  const isEditMode = !!existingRegistration?.id;

  const registrationSchema = object()
    .shape({
      id: string(),
      participantId: string(),
      competitionId: string().required(t('form.yup.genericRequired')),
      distanceId: string().required(t('form.yup.genericRequired')),
      ...(priceEditable
        ? { price: number().required(t('form.yup.genericRequired')).min(0) }
        : {}),
    })
    .required();

  const defaultValues: IFormRegistration = useMemo(
    () => ({
      id: existingRegistration?.id,
      participantId: selectedParticipant?.id ?? existingRegistration?.participantId,
      competitionId: resolveResourceId(existingRegistration?.competitionId),
      distanceId: resolveResourceId(existingRegistration?.distanceId),
      price: existingRegistration?.price ?? 0,
    }),
    [existingRegistration, selectedParticipant?.id],
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    watch,
    setValue,
    reset,
  } = useForm<IFormRegistration>({
    resolver: yupResolver(registrationSchema) as Resolver<IFormRegistration>,
    defaultValues,
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const onSubmit: SubmitHandler<IFormRegistration> = async registration => {
    if (priceEditable) {
      handleSave(registration);
      return;
    }
    const { price: _price, ...payload } = registration;
    handleSave(payload);
  };

  const competitionId = watch('competitionId');
  const distanceId = watch('distanceId');

  const computedPrice = useMemo(() => {
    if (!edition || !selectedParticipant) {
      return 0;
    }
    try {
      return calculateRegistrationPrice(
        edition,
        {
          dateOfBirth: selectedParticipant.dateOfBirth,
          age: selectedParticipant.age,
          discounted: selectedParticipant.discounted,
        },
        { isAdminContext: priceEditable },
      );
    } catch {
      return 0;
    }
  }, [edition, selectedParticipant, priceEditable]);

  const readOnlyDisplayPrice = useMemo(() => {
    if (!edition || !selectedParticipant) {
      return 0;
    }
    if (isEditMode && existingRegistration) {
      return (
        getRegistrationDisplayPrice(
          existingRegistration.price,
          edition,
          {
            dateOfBirth: selectedParticipant.dateOfBirth,
            age: selectedParticipant.age,
            discounted: selectedParticipant.discounted,
          },
        ) ?? computedPrice
      );
    }
    return computedPrice;
  }, [edition, selectedParticipant, isEditMode, existingRegistration, computedPrice]);

  // Auto-fill calculated price on create only. On edit, keep the stored (or
  // last manually overridden) price even if competition/distance changes.
  useEffect(() => {
    if (
      isEditMode ||
      !priceEditable ||
      !edition ||
      !selectedParticipant ||
      !competitionId ||
      !distanceId
    ) {
      return;
    }
    setValue('price', computedPrice, { shouldValidate: true });
  }, [
    isEditMode,
    priceEditable,
    edition,
    selectedParticipant,
    competitionId,
    distanceId,
    computedPrice,
    setValue,
  ]);

  if (!editionId || !competitionsById || !distancesById || !edition) {
    return null;
  }

  const editionCompetitions = edition.competitions || [];
  const registeredCompetitionId = resolveResourceId(existingRegistration?.competitionId);
  const registeredDistanceId = resolveResourceId(existingRegistration?.distanceId);
  const extraDistanceId =
    resolveResourceId(competitionId) === registeredCompetitionId ? registeredDistanceId : '';
  const competitionDistanceIds = distanceIdsForCompetition(
    editionCompetitions,
    competitionId,
    distancesById,
    extraDistanceId,
  );

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <DialogContent>
        <Controller
          name="competitionId"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth margin="dense" error={!!errors.competitionId}>
              <InputLabel id="registration-competition-label">{t('drawer.competitions')}</InputLabel>
              <Select
                {...field}
                value={field.value ?? ''}
                labelId="registration-competition-label"
                label={t('drawer.competitions')}
                onChange={event => {
                  field.onChange(event);
                  setValue('distanceId', '');
                }}
              >
                {editionCompetitions.map(comp => {
                  const competitionIdValue = resolveResourceId(comp.competitionId);
                  const competition = competitionsById[competitionIdValue];
                  return competition ? (
                    <MenuItem key={competitionIdValue} value={competitionIdValue}>
                      {competition.i18n[currentLang]}
                    </MenuItem>
                  ) : null;
                })}
              </Select>
            </FormControl>
          )}
        />
        <Controller
          name="distanceId"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth margin="dense" error={!!errors.distanceId}>
              <InputLabel id="registration-distance-label">{t('drawer.distances')}</InputLabel>
              <Select
                {...field}
                value={field.value ?? ''}
                labelId="registration-distance-label"
                label={t('drawer.distances')}
                disabled={!competitionId}
              >
                {competitionId &&
                  competitionDistanceIds.map(optionDistanceId => {
                    const distance = distancesById[optionDistanceId];
                    return distance ? (
                      <MenuItem key={optionDistanceId} value={optionDistanceId}>
                        {distance.i18n[currentLang]}
                      </MenuItem>
                    ) : null;
                  })}
              </Select>
            </FormControl>
          )}
        />
        {priceEditable ? (
          <Controller
            name="price"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                margin="dense"
                type="number"
                label={t('export.price')}
                error={!!errors.price}
                helperText={errors.price?.message}
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">{t('base.currency')}</InputAdornment>
                    ),
                  },
                }}
              />
            )}
          />
        ) : (
          <TextField
            fullWidth
            margin="dense"
            type="number"
            label={t('export.price')}
            value={readOnlyDisplayPrice}
            disabled
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">{t('base.currency')}</InputAdornment>
                ),
              },
            }}
          />
        )}
        <span>
          {t('form.needAccommodation') || 'Need accommodation?'}{' '}
          <Link component={RouterLink} to="/useful-info/accommodation" target="_blank">
            {t('form.viewAccommodationInfo') || 'View accommodation information'}
          </Link>
        </span>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('base.cancel')}</Button>
        <Button type="submit" variant="contained" disabled={!competitionId || !distanceId}>
          {isEditMode ? t('form.save') : t('registrations.register')}
        </Button>
      </DialogActions>
    </form>
  );
};

export default RegistrationForm;
